import HorizontalLine from '../ui/HorizontalLine';
import StyledFormSection from '../styled/StyledFormSection';
import StyledInput from '../styled/StyledInput';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import StyledSelect from '../styled/StyledSelect';
import { COUNTRIES, CURRENCIES } from '../../../../shared/CountriesData';
import StyledDropZone from '../styled/StyledDropZone';
import TarifList from '../tarifs/TarifList';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { Label } from '../ui/label';
import { BENEFIT_MARGIN_RATE_LABEL } from '@/constants/marginRateLabels';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import TranslateIcon from '../translation/TranslateIcon';
import { Control, useFormContext } from 'react-hook-form';
import { VehicleWithChauffeurFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';

interface VehicleWithChauffeurConfigurationProps {
  control: Control<VehicleWithChauffeurFormDataWithTranslations>;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  language: SupportedLocale;
  isEditEnabled: boolean;
  translateFields: (
    fields?: Array<
      'benefit.name' | 'model' | 'shortDescription' | 'longDescription'
    >,
  ) => Promise<void>;
  isTranslating: boolean;
  errorsLength: number;
}

const VehicleWithChauffeurConfiguration = ({
  control,
  suppliers,
  isEditEnabled,
  language,
  translateFields,
  isTranslating,
  errorsLength,
}: VehicleWithChauffeurConfigurationProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { setValue } =
    useFormContext<VehicleWithChauffeurFormDataWithTranslations>();

  const changeSupplierWithCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: 'supplier',
    secondaryName: 'currency',
    retrieveSecondary: (primaryValue) => {
      const supplier = suppliers?.find(
        (supplier) => supplier.id === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.value === supplier?.currency,
      );
      return currency;
    },
  });

  const suppliersOptions =
    suppliers?.map(({ id, name }) => ({ value: id, label: name })) ?? [];

  return (
    <>
      <StyledFormSection
        sectionTitle={t('forms.sections.generalInfo')}
        icon={
          <TranslateIcon
            translateFields={() => translateFields(['benefit.name'])}
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        {errorsLength > 0 && (
          <Label className="text-error-500">
            {t('common.checkRequiredFields')}
          </Label>
        )}
        <StyledTranslatableInput
          control={control}
          name="benefit.name"
          originalName="name"
          setValue={setValue}
          language={language}
          title={t('vehiclesWithChauffeur.name')}
          placeholder={t('vehiclesWithChauffeur.namePlaceholder')}
          disabled={!isEditEnabled}
        />
        <div className="flex flex-col md:flex-row gap-4">
          <StyledSelect
            options={suppliersOptions}
            control={control}
            name="supplier"
            title={t('forms.labels.supplier') + ' *'}
            placeholder="Ex: Your Private Trip"
            isClearable
            disabled={!isEditEnabled}
            onChange={changeSupplierWithCurrency}
          />
          <StyledSelect
            options={CURRENCIES}
            control={control}
            name="currency"
            title={t('forms.labels.currency') + ' *'}
            placeholder={t('forms.placeholders.selectCurrency')}
            disabled={!isEditEnabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <StyledSelect
            options={COUNTRIES}
            control={control}
            name="country"
            title={t('forms.labels.country') + ' *'}
            placeholder={t('forms.placeholders.selectCountry')}
            disabled={!isEditEnabled}
          />
          <MarginRateStyledInput
            control={control}
            name="marginRate"
            title={getTranslatedConst(BENEFIT_MARGIN_RATE_LABEL)}
            placeholder={t('forms.placeholders.addRate')}
            disabled={!isEditEnabled}
          />
        </div>
        {isEditEnabled && (
          <Label className="text-gray-700">
            {t('common.requiredFieldsNote')}
          </Label>
        )}
      </StyledFormSection>
      <HorizontalLine />
      <StyledFormSection
        sectionTitle={t('vehiclesWithChauffeur.vehicleInfo')}
        icon={
          <TranslateIcon
            translateFields={() =>
              translateFields(['model', 'shortDescription', 'longDescription'])
            }
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        <StyledTranslatableInput
          control={control}
          name="model"
          originalName="model"
          setValue={setValue}
          language={language}
          title={t('vehiclesWithChauffeur.model')}
          placeholder={t('vehiclesWithChauffeur.modelPlaceholder')}
          disabled={!isEditEnabled}
        />
        <StyledTranslatableInput
          control={control}
          name="shortDescription"
          originalName="shortDescription"
          setValue={setValue}
          language={language}
          title={t('vehiclesWithChauffeur.shortDescription')}
          placeholder={t('vehiclesWithChauffeur.shortDescriptionPlaceholder')}
          disabled={!isEditEnabled}
        />
        <StyledTranslatableTextarea
          control={control}
          originalName="longDescription"
          setValue={setValue}
          language={language}
          name="longDescription"
          title={t('vehiclesWithChauffeur.longDescription')}
          placeholder={t('vehiclesWithChauffeur.longDescriptionPlaceholder')}
          rows={3}
          disabled={!isEditEnabled}
        />
        <StyledDropZone
          control={control}
          name="images"
          title={t('forms.labels.gallery')}
          placeholder="PNG, JPG"
          multiple
          disabled={!isEditEnabled}
        />
        <StyledInput
          control={control}
          name="maxCapacity"
          type="number"
          title={t('vehiclesWithChauffeur.maxCapacity')}
          placeholder={t('vehiclesWithChauffeur.maxCapacityPlaceholder')}
          disabled={!isEditEnabled}
        />
      </StyledFormSection>
      <HorizontalLine />
      <TarifList
        control={control}
        benefitType={BenefitType.VEHICLE_WITH_CHAUFFEUR}
        isViewMode={!isEditEnabled}
      />
    </>
  );
};

export default VehicleWithChauffeurConfiguration;
